/**
 * LOYALTY PORTAL — the container.
 *
 * It owns the fetching, the session and the screen; every pixel is drawn by
 * components/loyalty/PortalView.jsx, the SAME component the Command Center's
 * live preview renders. The organization and the member are decided by the
 * SERVER from the card and the signed session; this page never sends an id of
 * its own.
 *
 * "Remember my email on this phone" stores ONLY the email and first name, in
 * local storage, wrapped in try/catch — never the PIN, never anything that
 * signs anyone in. The PIN is required every single time.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import PortalView from '../../components/loyalty/PortalView';
import { Screen } from '../../components/loyalty/ui';
import { handOverSession, heldSession, rememberMe, rememberedMe } from './session';
import { plain } from '../../lib/money';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api`;

const remembered = rememberedMe;
const remember = (value) => { rememberMe(value); if (!value) handOverSession(''); };

export default function LoyaltyPortal() {
    const { cardId } = useParams();
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);

    const [cfg, setCfg] = useState(null);
    const [screen, setScreen] = useState('loading');
    const [saved, setSaved] = useState(remembered());
    const [form, setForm] = useState({ first_name: '', email: '', pin: '', consent: false });
    const [me, setMe] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [askRemember, setAskRemember] = useState(false);
    const session = useRef('');
    const [notice, setNotice] = useState('');
    const [gifts, setGifts] = useState(null);

    useEffect(() => {
        fetch(`${API}/loyalty/public/${cardId}?lang=${lang}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then((d) => {
                setCfg(d);
                const mem = remembered();
                setSaved(mem);
                setForm((f) => ({ ...f, email: mem?.email || '' }));
                setScreen(mem?.email ? 'sign_in' : 'register');
            })
            .catch(() => setScreen('unavailable'));
    }, [cardId, lang]);

    const loadMe = useCallback(async (token) => {
        const r = await fetch(`${API}/loyalty/me`, { headers: { 'X-Loyalty-Session': token } });
        if (!r.ok) { setScreen('sign_in'); return false; }
        setMe(await r.json());
        return true;
    }, []);

    // A member who has just confirmed arrives already signed in: the session
    // was handed over, so the portal opens on Welcome and then home.
    useEffect(() => {
        if (!cfg) return;
        const token = heldSession();
        if (!token || session.current) return;
        session.current = token;
        (async () => {
            const ok = await loadMe(token);
            if (!ok) { handOverSession(''); session.current = ''; return; }
            // Straight from the front desk she has just said hello, so her own
            // home opens at once instead of a second welcome.
            if (new URLSearchParams(window.location.search).get('lounge') === '1') {
                setScreen('home');
                return;
            }
            setScreen('welcome_back');
            window.setTimeout(
                () => setScreen((cur) => (cur === 'welcome_back' ? 'home' : cur)), 5000);
        })();
    }, [cfg, loadMe]);

    const post = async (path, body, signed = false) => {
        setBusy(true); setError('');
        try {
            const r = await fetch(`${API}${path}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(signed ? { 'X-Loyalty-Session': session.current } : {}),
                },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                const reason = typeof data.detail === 'object' ? data.detail.reason : data.detail;
                setError(t(`loyalty.error.${reason}`, t('loyalty.error.generic', 'Something went wrong')));
                return null;
            }
            return data;
        } finally { setBusy(false); }
    };

    const loadGifts = useCallback(async () => {
        const token = session.current;
        if (!token) return;
        const r = await fetch(`${API}/loyalty/gift-cards?lang=${lang}`,
            { headers: { 'X-Loyalty-Session': token } });
        if (r.ok) setGifts(await r.json());
    }, [lang]);

    const on = {
        setForm,
        go: (next) => {
            setScreen(next);
            setError('');
            setNotice('');
            if (next === 'cash_in') setForm((f) => ({ ...f, value: '', cashed: null }));
            if (['gifts', 'bank', 'give', 'redeem', 'create', 'create_allowance',
                'points_card', 'history'].includes(next)) {
                loadGifts();
            }
        },
        giveAway: async (cardId) => {
            const out = await post(`/loyalty/gift-cards/${cardId}/give`,
                { friend_first_name: form.friend_first_name }, true);
            if (out) {
                setForm({ ...form, giving: null, friend_first_name: '' });
                await loadGifts();
            }
        },
        cashIn: async () => {
            // The amount SHE chose, in dollars. One card, or nothing at all.
            const out = await post('/loyalty/cash-in', { value: Number(plain(form.value)) }, true);
            if (out) {
                setForm({ ...form, value: '', cashed: out.card });
                await loadMe(session.current);
                await loadGifts();
            }
        },
        createGift: async () => {            const out = await post('/loyalty/gift-cards/create',
                { value: Number(plain(form.value)), friend_first_name: form.friend_first_name }, true);
            if (out) {
                setForm({ ...form, value: '', friend_first_name: '', created: out.card });
                await loadGifts();
            }
        },
        createGiftFromPoints: async () => {
            const out = await post('/loyalty/gift-cards/from-points',
                { value: Number(plain(form.value)), friend_first_name: form.friend_first_name }, true);
            if (out) {
                setForm({ ...form, value: '', friend_first_name: '', created: out.card });
                await loadMe(session.current);
                await loadGifts();
            }
        },
        register: async () => {
            const out = await post(`/loyalty/public/${cardId}/register`, { ...form, lang });
            if (out) setScreen('check_email');
        },
        resend: async () => {
            const out = await post(`/loyalty/public/${cardId}/resend`, { email: form.email, lang });
            if (out) setError(t('loyalty.resent', 'Sent again — check your email'));
        },
        signIn: async () => {
            const out = await post(`/loyalty/public/${cardId}/sign-in`,
                { email: saved?.email || form.email, pin: form.pin });
            if (!out) return;
            session.current = out.session;
            if (!saved?.email) setAskRemember(true);
            setScreen('welcome_back');
            await loadMe(out.session);
            // About 5 seconds, then home — but ONLY if the member is still on
            // the welcome screen, so a tap is never undone by the timer.
            window.setTimeout(() => setScreen((cur) => (cur === 'welcome_back' ? 'home' : cur)), 5000);
        },
        forgotPin: async () => {
            const out = await post(`/loyalty/public/${cardId}/pin-reset`,
                { email: saved?.email || form.email, lang });
            if (out) setNotice('reset_sent');
        },
        remember: (yes) => {
            const value = yes ? { email: form.email || saved?.email, first_name: me?.member?.first_name || '' } : null;
            remember(value); setSaved(value); setAskRemember(false);
        },
        notYou: () => {
            remember(null); setSaved(null);
            setForm((f) => ({ ...f, email: '', pin: '' }));
            setScreen('register');
        },
        signOut: () => {
            remember(null); setSaved(null); setMe(null);
            setForm({ first_name: '', email: '', pin: '', consent: false });
            setScreen('register');
        },
    };

    if (screen === 'loading') {
        return <Screen testid="loyalty-loading"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></Screen>;
    }
    if (screen === 'unavailable') {
        return (
            <Screen testid="loyalty-unavailable">
                <p className="text-center text-base opacity-80">
                    {t('loyalty.unavailable', "This card isn't set up yet")}
                </p>
            </Screen>
        );
    }

    return (
        <PortalView screen={screen} cfg={cfg} me={me} form={form} error={error} busy={busy}
                    saved={saved} askRemember={askRemember} notice={notice} gifts={gifts}
                    on={on} />
    );
}
